import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { debounce, isEqual } from 'lodash-es';
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { FormattedMessage } from 'react-intl';

import { Paragraph } from '@dynatrace/strato-components/typography';

import { DataTableV2Body } from './DataTableV2Body.js';
import { DataTableV2EmptyState } from './DataTableV2EmptyState.js';
import { DataTableV2Header } from './DataTableV2Header.js';
import { dataTableV2VirtualizationContainerCSS } from './DataTableV2VirtualizationContainer.sty.js';
import { useFontsUpdated } from '../../../core/hooks/useFontsUpdated.js';
import { addPinnedColumnsToVisibleRange } from '../features/ColumnPinning/column-pinning-utils.js';
import { LoadingState } from '../features/LoadingState.js';
import { CellActionFloatingMenu } from '../features/UserActions/FloatingMenu.js';
import { type Table } from '../hooks/useTable/types.js';

export function DataTableV2VirtualizationContainer<T>(
  props: Readonly<{
    table: Table<T>;
    emptyState: ReturnType<typeof DataTableV2EmptyState>;
    fullHeight: boolean;
  }>,
) {
  const { table, emptyState, fullHeight } = props;

  const parentRef = useRef<HTMLDivElement | null>(null);

  const rows = table.getRowModel().rows;
  const visibleColumns = table.getVisibleLeafColumns();
  const totalHeaderHeight = table._getTotalHeaderHeight();

  const getVisibleLeafColumnsWithoutBuiltin =
    table.getVisibleLeafColumnsWithoutBuiltin();
  const tableState = table.getState();

  const getUniqueRowKey = useCallback(
    (index: number) => rows[index].id,
    [rows],
  );

  const rowVirtualizer = useVirtualizer({
    count: rows?.length ?? 0,
    getScrollElement: () => parentRef.current,
    // Overscan of 5 was very ambitious. A single mouse scroll tick is usually
    // more than 5 condensed rows, so this is increased. In the future there could
    // be a possibility to make this dependent on the row density.
    overscan: 20,
    indexAttribute: 'data-row',
    paddingStart: totalHeaderHeight,
    // use double the header height to avoid a row being partially hidden, when navigating via page up and page down
    scrollPaddingStart: totalHeaderHeight * 2,
    scrollMargin: totalHeaderHeight,
    estimateSize: () => table._estimateRowHeight(),
    measureElement: (element) => element.getBoundingClientRect().height,
    // Default wait until the scrollEnd is triggered and it starts updating.
    // https://tanstack.com/virtual/latest/docs/api/virtualizer#isscrollingresetdelay
    // Reducing it from the default 150ms seems to improve perceived scroll performance
    isScrollingResetDelay: 20,
    getItemKey: getUniqueRowKey,
  });

  // Keep a list of columns that were shown in the virtualizer.
  const columnIdsShownByVirtualizer = useRef(new Set());
  const hasScrolledColumns = useRef(false);
  const remeasuredColumns = useRef<string[]>([]);

  // If the column definition changes, make sure that the measurements set is cleared again
  useEffect(() => {
    columnIdsShownByVirtualizer.current.clear();
    hasScrolledColumns.current = false;
  }, [table.options.columns]);

  const pinnedLeftColumns = table.getLeftVisibleLeafColumns();
  const pinnedRightColumns = table.getRightVisibleLeafColumns();

  const pinnedRightStartIndex =
    visibleColumns.length - pinnedRightColumns.length;

  // create right array indexes
  const pinnedRightIndexes = useMemo(
    () =>
      Array.from(
        { length: pinnedRightColumns.length },
        (_, i) => pinnedRightStartIndex + i,
      ),
    [pinnedRightColumns.length, pinnedRightStartIndex],
  );

  // create left array indexes
  const pinnedLeftIndexes = useMemo(
    () => Array.from(Array(pinnedLeftColumns.length).keys()),
    [pinnedLeftColumns.length],
  );

  const columnVirtualizer = useVirtualizer({
    count: visibleColumns.length,
    getScrollElement: () => parentRef.current,
    indexAttribute: 'data-column',
    estimateSize() {
      return 150;
    },
    rangeExtractor: useMemo(
      () => (range) =>
        addPinnedColumnsToVisibleRange(
          range,
          pinnedLeftIndexes,
          pinnedRightIndexes,
        ),
      [pinnedLeftIndexes, pinnedRightIndexes],
    ),
    overscan: 5,
    horizontal: true,
    onChange: (instance) => {
      // Set the flag that the user has scrolled to the right to true,
      // to no longer reset the column measurements when they are out of view.
      if (instance.scrollOffset !== null && instance.scrollOffset > 0) {
        hasScrolledColumns.current = true;
      }

      // OnChange is called a lot in the beginning and due to the resizing flashing of the
      // column items, the actual list of virtualized items changes a bit in the early stages
      // of the lifecycle. We keep track of the items that have been measured and rendered properly.
      // We initialize this only when no column scroll has happened yet.
      if (instance.scrollOffset === 0 && hasScrolledColumns.current === false) {
        const virtualItems = instance.getVirtualItems();
        if (virtualItems.length <= columnIdsShownByVirtualizer.current.size) {
          columnIdsShownByVirtualizer.current.clear();
        }
        for (const item of virtualItems) {
          const virtualColumn = visibleColumns[item.index];
          columnIdsShownByVirtualizer.current.add(virtualColumn.id);
        }
        return;
      }

      if (columnIdsShownByVirtualizer.current.size < visibleColumns.length) {
        const virtualItems = instance.getVirtualItems();
        const columnsToMeasure = virtualItems
          .map((virtualItem) => visibleColumns[virtualItem.index]?.id)
          .filter(Boolean)
          .filter((id) => !columnIdsShownByVirtualizer.current.has(id));

        if (!isEqual(columnsToMeasure, remeasuredColumns.current)) {
          table.remeasureColumn(columnsToMeasure);
          remeasuredColumns.current = columnsToMeasure;
        }
      }
    },
  });

  table.resetGridMeasurements();
  table.registerRowVirtualizer(rowVirtualizer, columnVirtualizer);

  const fontUpdateQueued = useRef(false);
  useFontsUpdated(() => {
    if (fontUpdateQueued.current === false) {
      fontUpdateQueued.current = true;
      // Create an abort controller in order to remove the eventListener
      // after the race has resolved.
      const controller = new AbortController();
      Promise.race([
        // If the animations have not stopped after a timeout period,
        // we can remeasure never the less.
        new Promise<void>((resolve) =>
          setTimeout(() => {
            resolve();
          }, 1_000),
        ),
        // If the animations subside within a reasonable interval, we
        // can also remeasure.
        new Promise<void>((resolve) => {
          document.addEventListener(
            'animationend',
            debounce(() => {
              resolve();
            }, 200),
            { signal: controller.signal },
          );
        }),
      ]).then(() => {
        const virtualizedVisibleColumns = columnVirtualizer.getVirtualItems();
        const columnIds = virtualizedVisibleColumns
          .map((virtualItem) => visibleColumns[virtualItem.index]?.id)
          .filter(Boolean);

        table.remeasureColumn(columnIds);
        table._queue(() => {
          table.setState((state) => ({ ...state }));
        });
        controller.abort();
        fontUpdateQueued.current = false;
      });
    }
  });

  const emptyStateComponent = emptyState ?? <DataTableV2EmptyState />;
  const isDataEmpty = rowVirtualizer.options.count === 0;
  const isColumnsEmpty = columnVirtualizer.options.count === 0;
  const isEmpty = isDataEmpty || isColumnsEmpty;
  const hasVisibleColumns = getVisibleLeafColumnsWithoutBuiltin.length > 0;

  const gridHeight = isEmpty ? 'auto' : rowVirtualizer.getTotalSize();
  const visualVariantOptions = table.getVisualVariantOptions();

  const estimatedRowHeight = table._estimateRowHeight();
  const containerStyle = useMemo(() => {
    let minHeight: CSSProperties['minHeight'] = undefined;
    if (totalHeaderHeight === 0) {
      // Be at least two rows tall when no header is given
      minHeight = estimatedRowHeight * 2;
    } else {
      // or header size + 1 additional row
      minHeight = totalHeaderHeight + estimatedRowHeight;
    }

    if (visualVariantOptions.contained === true) {
      // Add a borderTop and border bottom for the borders around the containment.
      minHeight += 4;
    }
    const baseStyle: CSSProperties = {
      minHeight,
    };

    if (fullHeight) {
      baseStyle.height = '100%';
    }
    return baseStyle;
  }, [
    totalHeaderHeight,
    visualVariantOptions.contained,
    estimatedRowHeight,
    fullHeight,
  ]);

  const scrollRect = rowVirtualizer.scrollRect;
  const scrollElement = rowVirtualizer.scrollElement;
  const scroll = useMemo(() => {
    if (scrollRect && scrollElement) {
      // Determine whether the scrollbars are visible to apply the according styles for the scrollbars
      const hasScrollbarX =
        scrollRect.width <
        (scrollElement.children[0] as HTMLDivElement)?.offsetWidth;
      const hasScrollbarY =
        scrollRect.height <
        (scrollElement.children[0] as HTMLDivElement)?.offsetHeight;

      return hasScrollbarX && hasScrollbarY
        ? 'xy'
        : hasScrollbarX
        ? 'x'
        : hasScrollbarY
        ? 'y'
        : false;
    }
  }, [scrollElement, scrollRect]);

  const draggedHeaderColumn = table.getState().draggedHeaderColumn;
  const draggedRowId = table.getState().draggingRowId;
  const isDraggingRow = !!draggedRowId;

  useEffect(() => {
    // In a scrollable table, when ordering columns via drag and drop, dragging automatically scrolls the content.
    // This effect ensures that vertical scrolling is prevented while a column is being dragged.
    const parent = parentRef.current;
    const currentOffset = rowVirtualizer.scrollOffset;
    const abort = new AbortController();
    if (draggedHeaderColumn) {
      // Add an event listener to the parent element to maintain the current scroll position
      parent?.addEventListener(
        'scroll',
        () => rowVirtualizer.scrollToOffset(currentOffset ?? 0),
        { signal: abort.signal },
      );
    }
    return () => {
      abort.abort();
    };
  }, [draggedHeaderColumn, rowVirtualizer]);

  useEffect(() => {
    // In a scrollable table, when ordering rows via drag and drop, dragging automatically scrolls the content horizontally.
    // This effect ensures that horizontal scrolling is prevented while a row is being dragged.
    const parent = parentRef.current;
    const currentOffset = columnVirtualizer.scrollOffset;
    const abort = new AbortController();
    if (draggedRowId) {
      // Add an event listener to the parent element to maintain the current scroll position
      parent?.addEventListener(
        'scroll',
        () => columnVirtualizer.scrollToOffset(currentOffset ?? 0),
        { signal: abort.signal },
      );
    }
    return () => {
      abort.abort();
    };
  }, [columnVirtualizer, draggedRowId]);

  // Create the grid template rows
  const measurements = rowVirtualizer.measurementsCache;
  let templateRows = table.getHeaderGridTemplateRows();

  if (measurements.length > 0) {
    const bodyRows = measurements
      .map(({ size }) => `minmax(min-content, ${size}px)`)
      .join(' ');
    templateRows = `${templateRows} ${bodyRows} [body-end]`;
  }

  return (
    <div
      ref={parentRef}
      className={clsx(
        'strato-table-virtualization-container',
        dataTableV2VirtualizationContainerCSS({
          hasVisibleColumns: hasVisibleColumns,
          contained: visualVariantOptions.contained,
          rowSeparation: visualVariantOptions.rowSeparation,
          headers: visualVariantOptions.headers,
          loading: table.getState().loading,
          scroll: scroll,
        }),
      )}
      style={containerStyle}
    >
      {hasVisibleColumns && (
        <div
          role="grid"
          style={{
            display: 'grid',
            height: gridHeight,
            gridTemplateColumns: table.getTableGridTemplate(),
            gridTemplateRows: templateRows,
          }}
          onMouseOver={(event) => {
            if (!isDraggingRow) {
              table.getInteractiveRowMouseOverHandler()(event);
            }
          }}
          onMouseLeave={(event) => {
            table.getInteractiveRowMouseLeaveHandler()(event);
          }}
          onMouseDown={(event) => {
            if (
              // main button (left button)
              event.button === 0 ||
              // auxiliary button (wheel/middle button)
              event.button === 1
            ) {
              table.getInteractiveRowClickHandler()(event);
            }
          }}
          onContextMenu={(event) => {
            // disable context menu for links, we need to show the browser menu instead
            if (event.target instanceof Element && event.target.closest('a')) {
              return;
            }

            table.getUserActionFloatingMenuContextMenuHandler()(event);
          }}
        >
          {visualVariantOptions.headers === 'default' ? (
            <DataTableV2Header />
          ) : null}
          <DataTableV2Body
            rows={rows}
            rowVirtualizer={rowVirtualizer}
            columnVirtualizer={columnVirtualizer}
          />
          {(table.options.enableCellActions ||
            table.options.enableColumnActions) && <CellActionFloatingMenu />}
        </div>
      )}

      {/* Show default message if all columns are hidden */}
      {getVisibleLeafColumnsWithoutBuiltin.length === 0 &&
        !isEmpty &&
        !tableState.loading && (
          <div style={{ gridRow: 2, placeContent: 'center' }}>
            <Paragraph style={{ textAlign: 'center' }}>
              <FormattedMessage
                defaultMessage="No table columns visible."
                id="0hFQ7cTaFNP7qUY3"
                description="When all columns are hidden, this is shown in the main table area."
              />
            </Paragraph>
          </div>
        )}

      {/* Show empty state message if there is no data or if the columns configuration is empty */}
      {!tableState.loading && isEmpty && emptyStateComponent}

      {tableState.loading && <LoadingState headerHeight={totalHeaderHeight} />}
    </div>
  );
}
